import { useEffect, useRef, useState } from 'react';

import { useScrollToError } from '../hooks/useScrollToError.ts';
import { useStableValidateMessage } from '../hooks/useStableValidateMessage.ts';
import '../knowledge-radios.css';
import { getContainerClasses } from '../utils/inputStyles';
import CheckboxItem from './CheckboxItem';
import FieldValidationMessage from './FieldValidationMessage.tsx';

interface DataSourceItem {
  key: string;
  text: string;
}

interface IProps {
  label: string;
  name: string;
  disabled?: boolean;
  options: DataSourceItem[];
  value: string[];
  exclusiveKeys?: string[];
  updateFieldValue?: (value: string[]) => void;
  triggerFieldChange?: (value: string[]) => void;
  onSelectionChange?: (next: string[]) => void;
  validatemessage?: string;
}

function selectionKey(keys: string[]): string {
  return [...keys].sort().join('|');
}

function resolveSelection(selectedValues: string[], key: string, checked: boolean, exclusiveKeys: string[]): string[] {
  if (!checked) {
    return selectedValues.filter((v) => v !== key);
  }

  if (exclusiveKeys.includes(key)) {
    return [key];
  }

  const withoutExclusive = selectedValues.filter((v) => !exclusiveKeys.includes(v));
  return withoutExclusive.includes(key) ? withoutExclusive : [...withoutExclusive, key];
}

function CheckboxGroup(props: IProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>(props.value);
  const [dismissedMessage, setDismissedMessage] = useState<string | undefined>(undefined);
  const isDisabled = !!props.disabled;
  const stableMessage = useStableValidateMessage(props.validatemessage);
  const validateMessage = stableMessage === dismissedMessage ? undefined : stableMessage;
  const hasError = !!validateMessage;
  const rootRef = useScrollToError(validateMessage, [props.value, props.updateFieldValue, props.onSelectionChange]);

  const incomingSelectionKey = selectionKey(props.value);
  const appliedSelectionKeyRef = useRef(incomingSelectionKey);

  useEffect(() => {
    if (appliedSelectionKeyRef.current === incomingSelectionKey) return;
    appliedSelectionKeyRef.current = incomingSelectionKey;
    setSelectedValues(props.value);
  }, [incomingSelectionKey, props.value]);

  const handleChange = (key: string, checked: boolean) => {
    if (isDisabled) return;

    const newValues = resolveSelection(selectedValues, key, checked, props.exclusiveKeys ?? []);

    setSelectedValues(newValues);
    appliedSelectionKeyRef.current = selectionKey(newValues);
    setDismissedMessage(newValues.length > 0 ? stableMessage : undefined);

    if (props.onSelectionChange) {
      props.onSelectionChange(newValues);
      return;
    }

    props.updateFieldValue?.(newValues);
    props.triggerFieldChange?.(newValues);
  };

  const containerClasses = getContainerClasses({});
  const options = Array.isArray(props.options) ? props.options : [];
  const errorClass = hasError ? 'kr-radio-error' : '';

  return (
    <div ref={rootRef} className={containerClasses}>
      {props.label && <label className="!text-base">{props.label}</label>}
      <div className={`radio-list ${errorClass} ${isDisabled ? 'radio-list-disabled' : ''} w-full`}>
        {options.map((option) => (
          <CheckboxItem
            key={option.key}
            id={`${props.name}-${option.key}`}
            checked={selectedValues.includes(option.key)}
            caption={option.text}
            disabled={isDisabled}
            hasError={hasError}
            variant="tile"
            onChange={(checked) => handleChange(option.key, checked)}
          />
        ))}
      </div>
      <FieldValidationMessage message={validateMessage} />
    </div>
  );
}

export default CheckboxGroup;
